import { useState, useCallback } from "react";
import { useBackButton } from "@/hooks/useBackButton";
import { useCart } from "@/contexts/CartContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { QrCode, FileText, CreditCard, Calendar, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { trackInitiateCheckout } from "@/utils/metaPixel";

const creditRates: Record<number, number> = {
  1: 3.15, 2: 5.39, 3: 6.12, 4: 6.85, 5: 7.57, 6: 8.28,
  7: 8.99, 8: 9.69, 9: 10.38, 10: 11.06, 11: 11.74, 12: 12.40,
};

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shippingFee?: number;
  shippingLocation?: { name: string; city: string } | null;
}

const CheckoutDialog = ({ open, onOpenChange, shippingFee = 0, shippingLocation }: CheckoutDialogProps) => {
  const closeDialog = useCallback(() => onOpenChange(false), [onOpenChange]);
  useBackButton(open, closeDialog, "checkout");
  const { items, totalPrice, clearCart } = useCart();
  const { toast } = useToast();
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("pix");
  const [creditInstallments, setCreditInstallments] = useState(1);
  const [carneInstallments, setCarneInstallments] = useState(12);

  const phoneItems = items.filter(i => !i.id.startsWith("seguro-"));
  const insuranceItems = items.filter(i => i.id.startsWith("seguro-"));
  const phoneTotal = phoneItems.reduce((sum, i) => sum + i.priceNum * i.quantity, 0);
  const insuranceTotal = insuranceItems.reduce((sum, i) => sum + i.priceNum * i.quantity, 0);

  const pixPrice = phoneTotal * 0.95 + insuranceTotal + shippingFee;
  const boletoPrice = phoneTotal * 0.97 + insuranceTotal + shippingFee;
  const carnePrice = phoneTotal * 2 + shippingFee;

  const getFinalPrice = () => {
    switch (selectedMethod) {
      case "pix": return pixPrice;
      case "boleto": return boletoPrice;
      case "carne": return carnePrice;
      case "credito": {
        const rate = creditRates[creditInstallments] / 100;
        return phoneTotal * (1 + rate) + insuranceTotal + shippingFee;
      }
      default: return phoneTotal + insuranceTotal;
    }
  };

  const creditOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const carneOptions = [3, 6, 9, 12];

  const handleSubmit = async () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      toast({ title: "Preencha seus dados", description: "Nome e telefone são obrigatórios.", variant: "destructive" });
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    trackInitiateCheckout(getFinalPrice(), items.reduce((s, i) => s + i.quantity, 0));

    // Carnê → redirect to WhatsApp
    if (selectedMethod === "carne") {
      const productList = items
        .filter(i => !i.id.startsWith("seguro-"))
        .map(i => `• ${i.name} (x${i.quantity})`)
        .join("\n");
      const total = formatCurrency(carnePrice);
      const parcela = formatCurrency(carnePrice / carneInstallments);
      const msg = encodeURIComponent(
        `Olá! Gostaria de comprar via *Carnê*.\n\n` +
        `*Cliente:* ${customerName.trim()}\n` +
        `*Telefone:* ${customerPhone.trim()}\n\n` +
        `*Produtos:*\n${productList}\n\n` +
        `*Total:* ${total}\n` +
        `*Parcelas:* ${carneInstallments}x de ${parcela}\n\n` +
        `Aguardo instruções para finalizar a compra!`
      );
      window.open(`https://wa.me/5527981674787?text=${msg}`, "_blank");
      toast({ title: "Redirecionado ao WhatsApp", description: "Finalize sua compra via Carnê pelo WhatsApp." });
      setSubmitting(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          items: items.map(i => ({ id: i.id, name: i.name, priceNum: i.priceNum, quantity: i.quantity })),
          paymentMethod: selectedMethod,
          creditInstallments,
          shippingFee,
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          shippingLocation: shippingLocation ? { name: shippingLocation.name, city: shippingLocation.city } : null,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        clearCart();
        window.location.href = data.url;
      } else {
        throw new Error("Nenhuma resposta válida do checkout");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      const msg = err?.message || "Erro ao processar pagamento";
      if (msg.includes("duplicado") || msg.includes("duplicate")) {
        toast({ title: "Pedido já registrado", description: "Aguarde alguns minutos antes de tentar novamente.", variant: "destructive" });
      } else {
        toast({ title: "Erro no checkout", description: msg, variant: "destructive" });
      }
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSubmitting(false);
    setCustomerName("");
    setCustomerPhone("");
    setSelectedMethod("pix");
    setCreditInstallments(1);
    setCarneInstallments(12);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-foreground">Finalizar Compra</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Customer info */}
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Seu nome completo"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none"
              maxLength={100}
            />
            <input
              type="tel"
              placeholder="WhatsApp (ex: 27 99999-9999)"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none"
              maxLength={20}
            />
          </div>

          {/* Payment method tabs */}
          <Tabs value={selectedMethod} onValueChange={setSelectedMethod}>
            <TabsList className="w-full bg-secondary">
              <TabsTrigger value="pix" className="flex-1 text-xs gap-1"><QrCode className="w-3 h-3" />Pix</TabsTrigger>
              <TabsTrigger value="boleto" className="flex-1 text-xs gap-1"><FileText className="w-3 h-3" />Boleto</TabsTrigger>
              <TabsTrigger value="credito" className="flex-1 text-xs gap-1"><CreditCard className="w-3 h-3" />Crédito</TabsTrigger>
              <TabsTrigger value="carne" className="flex-1 text-xs gap-1"><Calendar className="w-3 h-3" />Carnê</TabsTrigger>
            </TabsList>

            <TabsContent value="pix" className="space-y-3">
              <div className="text-center space-y-2">
                <p className="text-2xl font-bold text-gradient-gold">{formatCurrency(pixPrice)}</p>
                <p className="text-xs text-primary">5% de desconto no Pix</p>
                {insuranceTotal > 0 && (
                  <p className="text-xs text-muted-foreground">Seguro incluso sem juros: {formatCurrency(insuranceTotal)}/mês</p>
                )}
                <p className="text-sm text-muted-foreground">Você será redirecionado para pagamento seguro via Stripe.</p>
              </div>
            </TabsContent>

            <TabsContent value="boleto" className="space-y-3">
              <p className="text-2xl font-bold text-gradient-gold text-center">{formatCurrency(boletoPrice)}</p>
              {insuranceTotal > 0 && (
                <p className="text-xs text-primary text-center">Seguro incluso sem juros: {formatCurrency(insuranceTotal)}/mês</p>
              )}
              <p className="text-xs text-muted-foreground text-center">
                O boleto será gerado automaticamente pelo Stripe com vencimento em 3 dias.
              </p>
            </TabsContent>

            <TabsContent value="credito" className="space-y-3">
              {(() => {
                const rate = creditRates[creditInstallments] / 100;
                const creditTotal = phoneTotal * (1 + rate) + insuranceTotal + shippingFee;
                const parcelaValue = creditTotal / creditInstallments;
                return (
                  <>
                    <p className="text-2xl font-bold text-gradient-gold text-center">{formatCurrency(creditTotal)}</p>
                    <p className="text-xs text-center text-muted-foreground">
                      {creditInstallments}x de {formatCurrency(parcelaValue)} ({creditRates[creditInstallments]}% sobre o aparelho)
                    </p>
                    {insuranceTotal > 0 && (
                      <p className="text-xs text-center text-primary">Seguro incluso sem juros: {formatCurrency(insuranceTotal)}/mês</p>
                    )}
                  </>
                );
              })()}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">Parcelas:</p>
                <div className="grid grid-cols-4 gap-2">
                  {creditOptions.map((n) => (
                    <button
                      key={n}
                      onClick={() => setCreditInstallments(n)}
                      className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                        creditInstallments === n
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-secondary border-border text-foreground hover:border-primary"
                      }`}
                    >
                      {n}x
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">Pagamento seguro via Stripe.</p>
            </TabsContent>

            <TabsContent value="carne" className="space-y-3">
              <p className="text-2xl font-bold text-gradient-gold text-center">{formatCurrency(carnePrice)}</p>
              <p className="text-xs text-center text-muted-foreground">
                {carneInstallments}x de {formatCurrency(carnePrice / carneInstallments)}
              </p>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">Parcelas:</p>
                <div className="grid grid-cols-4 gap-2">
                  {carneOptions.map((n) => (
                    <button
                      key={n}
                      onClick={() => setCarneInstallments(n)}
                      className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                        carneInstallments === n
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-secondary border-border text-foreground hover:border-primary"
                      }`}
                    >
                      {n}x
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">Você será redirecionado ao WhatsApp para finalizar.</p>
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-xs text-foreground">
                ⚠️ Carnê inclui <strong>Seguro obrigatório (R$ 49,99/mês)</strong>
              </div>
            </TabsContent>
          </Tabs>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-3 rounded-lg bg-gradient-gold text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processando...
              </>
            ) : selectedMethod === "carne" ? (
              "Continuar no WhatsApp"
            ) : (
              "Pagar com Stripe"
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDialog;
